import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { of } from "rxjs";
import { appConfig, clientConfig } from "../constant/app.constant";
import { TranslateService } from "@ngx-translate/core";

@Injectable({ providedIn: "root" })

export class TranslationDataService {

  public translationDataEmitter: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  defaultFiles = `${appConfig.NG_APP_ROOT}/assets/i18n/defaults/`;
  tenantFiles = `${clientConfig.MERCHANTMEDIAURL}/tenants/${clientConfig.TENANTNAME}/i18n/`;
  merchantFiles = `${clientConfig.MERCHANTMEDIAURL}/tenants/${clientConfig.TENANTNAME}/merchants/${clientConfig.MERCHANTNAME}/i18n/`;

  constructor(private http: HttpClient, private translateService: TranslateService) { }

  emitTranslationData(data: any) {
    if (data['en-GB']) this.translationDataEmitter.next(data['en-GB'].label.returnReasons);
    else this.translationDataEmitter.next(data.label.returnReasons);
  }

  checkFileExists(filePath: string) {
    return this.http.head(filePath, { observe: 'response' })
      .pipe(
        map(response => response.status === 200),
        catchError(() => of(false))
      );
  }

  fetchTranslationData(language: string) {
    if (clientConfig.MERCHANTCONFIG_IN_DB && clientConfig.MERCHANTCONFIG_IN_DB.toLowerCase().trim() === "true") {
      let returnReasons = this.translateService.instant(`label.returnReasons`);

      if (returnReasons && Array.isArray(returnReasons)) {
        this.translationDataEmitter.next(returnReasons);
      }
      else {
        this.translateService.getTranslation(language).subscribe((data) => {
          if (data && data.label && data.label.returnReasons)
            this.emitTranslationData(data);
          else
            console.error(`Error fetching return reason translation data for ${language}:`);
        },
        (error) => {
          console.error(`Error fetching translation data for ${language}:`, error);
        });
      }
    }
    else {
      const filePath = `${this.merchantFiles}${language}.json`;
      this.checkFileExists(filePath).subscribe(exists => {
        if (exists) {
          this.http.get(filePath).subscribe(
            (data: any) => {
              if (data && data.label && data.label.returnReasons)
                this.emitTranslationData(data);
              else
                this.fetchFallbackTranslationData(language);
            }
          );
        } else {
          this.fetchFallbackTranslationData(language);
        }
      });
    }
  }

  fetchFallbackTranslationData(language: string): any {
    const filePath = `${this.tenantFiles}${language}.json`;

    this.checkFileExists(filePath).subscribe(exists => {
      if (exists) {
        this.http.get(filePath).subscribe(
          (data: any) => {
            if (data && data.label && data.label.returnReasons)
              this.emitTranslationData(data);
            else
              this.fetchDefaultTranslationData(language);
          }
        );
      } else {
        this.fetchDefaultTranslationData(language);
      }
    });
  }

  fetchDefaultTranslationData(language: string): any {
    const filePath = `${this.defaultFiles}${language}.json`;

    this.checkFileExists(filePath).subscribe(exists => {
      if (exists) {
        this.http.get(filePath).subscribe(
          (data: any) => {
            if (data && data.label && data.label.returnReasons)
              this.emitTranslationData(data);
          },
          (error: any) => {
            console.error(`Error fetching translation data for ${language}:`, error);
          }
        );
      } else {
        console.error(`Default translation file for ${language} not found.`);
      }
    });
  }

  checkCarrierShippingInstructionsExists(type: string, deliveryOption: string): any {
    const carrierInstructions = this.translateService.instant(`shippingOptions.${type}.carrierShippingInstructions`);
    let instruction: any;

    /* If ngx-translate service doesn't find translation value for the provided key, it will return key as it is as a string,
      but carrierShippingInstructions is an array so we need to make sure we receive translation value
      i.e. instructions array and then only return that otherwise return false. */
    if (Array.isArray(carrierInstructions))
      instruction = carrierInstructions.find(instr => instr.modeOfTransport === deliveryOption);
    return instruction ? instruction : false;
  }

  getCarrierShippingInstruction(instructionKey: string, type: string, deliveryOption: string): string {
    const carrierInstructions = this.checkCarrierShippingInstructionsExists(type, deliveryOption);

    return carrierInstructions ? carrierInstructions[instructionKey] : null;
  }

  getShippingInstructions(instructionKey: string, type: string, deliveryOption: string): string {
    const carrierInstruction = this.getCarrierShippingInstruction(instructionKey, type, deliveryOption);
    if (carrierInstruction) {
      return carrierInstruction;
    } else {
      return this.translateService.instant(`shippingOptions.${type}.defaultShippingInstructions.${instructionKey}`);
    }
  }

  loadReasons(lang: string): Observable<any> {
    const resolvedLang = lang === 'en' ? 'en-GB' : lang;
    const filePath = `${clientConfig.MERCHANTMEDIAURL}/tenants/${clientConfig.TENANTNAME}/merchants/${clientConfig.MERCHANTNAME}/i18n/${resolvedLang}.json`;

    const noCacheUrl = `${filePath}?_=${Date.now()}`;

    return this.http.get<any>(
      noCacheUrl,
      {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    ).pipe(
      map(data => data.label?.returnReasons || null)
    );
  }
  
}
